/**
 * Custom Next.js Image loader for /uploads/ assets.
 *
 * Strategy: Sharp pre-generates variants at upload time (and via backfill script
 * for existing images). This loader maps the requested width to the nearest
 * pre-built variant filename. Result: mobile users download ~50 KB instead of
 * ~135 KB without any server-side resize at request time — Next.js Image
 * optimization stays disabled (see next.config.js).
 *
 * Variant filename convention:
 *   /uploads/products/foo.webp        → 1920w (the original, capped at 1920 by upload)
 *   /uploads/products/foo-w1080.webp  → 1080w
 *   /uploads/products/foo-w640.webp   → 640w
 *   /uploads/products/foo-w320.webp   → 320w
 *
 * For paths that aren't under /uploads/ (e.g. /images/logo/*), this loader
 * passes through unchanged.
 */

const VARIANT_WIDTHS = [320, 640, 1080] as const;
const MAX_WIDTH = 1920;

type LoaderArgs = {
  src: string;
  width: number;
  quality?: number;
};

export function uploadsImageLoader({ src, width }: LoaderArgs): string {
  // External URLs — return as-is
  if (/^https?:\/\//i.test(src)) return src;

  // Only apply variant logic to /uploads/ assets (where we control file generation)
  if (!src.startsWith('/uploads/')) return src;

  // Strip query string if present (we'll add cache-busting elsewhere)
  const [path] = src.split('?');

  // Already a variant? Don't re-add a -w suffix
  if (/-w(320|640|1080)\.[a-z0-9]+$/i.test(path)) return path;

  // Pick the smallest variant that satisfies the requested width.
  // If requested > 1080, serve the original (capped at 1920w by upload).
  const target = VARIANT_WIDTHS.find((w) => width <= w);
  if (!target) return path; // Use original

  // Variants are always written to disk as .webp regardless of source format
  // (see scripts/generate-image-variants.js and lib/utils/fileUpload.client.ts).
  // So `foo.png` → `foo-w640.webp`, never `foo-w640.png`. Previously we kept
  // the source extension, which 404'd for every non-WebP catalog image on
  // mobile (mobile-only because that's where srcset picks the variant URL).
  return path.replace(/(\.[a-z0-9]+)$/i, `-w${target}.webp`);
}

/**
 * Helper to derive what the variant URL would be for a given upload path.
 * Useful for building <picture><source srcset> manually outside of <Image>.
 */
export function variantUrl(uploadPath: string, width: 320 | 640 | 1080 | 1920): string {
  if (!uploadPath.startsWith('/uploads/')) return uploadPath;
  if (width === MAX_WIDTH) return uploadPath; // Original = 1920w
  return uploadPath.replace(/(\.[a-z0-9]+)$/i, `-w${width}$1`);
}

export const IMAGE_VARIANT_WIDTHS = VARIANT_WIDTHS;
export const IMAGE_MAX_WIDTH = MAX_WIDTH;

export default uploadsImageLoader;
